import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  FileText, Cloud, Recycle, Leaf, Users, Heart, Shield, Truck,
  Target, CheckCircle2, ChevronRight, ChevronLeft, CheckCircle,
  AlertCircle, AlertTriangle, Loader2, Building2, Mail, User,
  Briefcase, DollarSign, TrendingUp, Zap, Clock, Download,
  ArrowRight, Home, Award, Rocket, PieChart, Globe, MapPin,
  MessageSquare, Send, ChevronDown, ChevronUp, Sparkles, Link2,
  Bot, ExternalLink, ArrowUpRight, Calendar, UserPlus, Coins, Flag
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ReportSecurityNote } from '../components/ReportSecurityNote';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import axios from 'axios';
import { submitMaturity } from '../utils/maturityJobs';
import { generateSustainabilityPptx } from '../utils/generateSustainabilityPptx';
import AGIInsightsTab from '../components/agi/AGIInsightsTab';

const API = process.env.REACT_APP_BACKEND_URL;

const SustainabilityPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assessmentIdFromUrl = searchParams.get('id');
  
  // Assessment state
  const [step, setStep] = useState(0);
  const [dimensions, setDimensions] = useState([]);
  const [regions, setRegions] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [companySizes, setCompanySizes] = useState([]);
  const [revenueRanges, setRevenueRanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAssessment, setLoadingAssessment] = useState(false);
  const [error, setError] = useState('');
  const [responses, setResponses] = useState({});
  const [results, setResults] = useState(null);
  const [fullResults, setFullResults] = useState(null);
  const [secretCode, setSecretCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [assessmentId, setAssessmentId] = useState(assessmentIdFromUrl || null);
  
  // URL scraping state
  const [scrapingUrl, setScrapingUrl] = useState(false);
  const [scrapedData, setScrapedData] = useState(null);
  
  // Chatbot state
  const [showChatbot, setShowChatbot] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSessionId, setChatSessionId] = useState('');
  const chatEndRef = useRef(null);
  
  // Additional context
  const [showContextInput, setShowContextInput] = useState(false);
  const [additionalContext, setAdditionalContext] = useState('');
  
  // Profile state
  const [profile, setProfile] = useState({
    company_name: '',
    company_url: '',
    industry: '',
    region: '',
    company_size: '',
    annual_revenue: '',
    respondent_name: '',
    respondent_role: '',
    respondent_email: ''
  });

  // Dimension icons mapping
  const dimensionIcons = {
    'FileText': FileText,
    'Cloud': Cloud,
    'Recycle': Recycle,
    'Leaf': Leaf,
    'Users': Users,
    'Heart': Heart,
    'Shield': Shield,
    'Truck': Truck,
    'Target': Target,
    'CheckCircle2': CheckCircle2
  };

  // Fetch questions on mount
  useEffect(() => {
    fetchQuestions();
  }, []);
  
  // Load assessment from URL if id parameter is present
  useEffect(() => {
    if (assessmentIdFromUrl) {
      loadAssessmentFromUrl(assessmentIdFromUrl);
    }
  }, [assessmentIdFromUrl]);

  // Scroll chatbot
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);
  
  const loadAssessmentFromUrl = async (id) => {
    setLoadingAssessment(true);
    try {
      const res = await axios.get(`${API}/api/sustainability/assessment/${id}`);
      if (res.data) {
        setAssessmentId(id);
        
        const assessment = res.data;
        // Check both code_verified and is_sample_preview flags
        const isSamplePreview = assessment.is_sample_preview === true;
        const isVerified = assessment.code_verified === true || !isSamplePreview;
        
        // Store email in profile for verification
        if (assessment.respondent_email) {
          setProfile(prev => ({ ...prev, respondent_email: assessment.respondent_email }));
        }
        
        setResults({
          success: true,
          assessment_id: id,
          id: id,
          respondent_email: assessment.respondent_email,
          requires_secret_code: !isVerified,
          is_sample_preview: isSamplePreview,
          // Direct fields for renderResults compatibility
          company_name: assessment.company_name,
          industry: assessment.industry,
          overall_score: assessment.overall_score,
          maturity_level: assessment.maturity_level,
          overall_maturity_level: assessment.maturity_level?.name || assessment.overall_maturity_level,
          maturity_color: assessment.maturity_level?.color || '#10b981',
          industry_benchmark: assessment.industry_benchmark,
          dimension_scores: assessment.dimension_scores || [],
          regional_framework: assessment.regional_framework,
          sample_results: {
            company_name: assessment.company_name,
            industry: assessment.industry,
            overall_score: assessment.overall_score,
            overall_maturity_level: assessment.maturity_level?.name || assessment.maturity_level,
            maturity_color: assessment.maturity_level?.color || '#10b981',
            industry_benchmark: assessment.industry_benchmark?.avg || 2.5,
            top_performer_score: assessment.industry_benchmark?.top || 4.0,
            dimension_scores: assessment.dimension_scores || [],
            total_dimensions_count: assessment.total_dimensions_count || assessment.dimension_scores?.length || 8,
            executive_summary_preview: (assessment.ai_analysis?.executive_summary || '').substring(0, 300) + '...'
          }
        });
        
        // Only set full results if verified and not a sample preview
        if (isVerified && !isSamplePreview) {
          setFullResults(assessment);
        }
        
        setStep(999);
      }
    } catch (err) {
      console.error('Error loading assessment:', err);
      setError('Assessment not found or has expired. Please start a new assessment.');
    } finally {
      setLoadingAssessment(false);
    }
  };

  const fetchQuestions = async () => {
    try {
      const res = await axios.get(`${API}/api/sustainability/questions`);
      setDimensions(res.data.dimensions || []);
      setRegions(res.data.regions || []);
      setIndustries(res.data.industries || []);
      setCompanySizes(res.data.company_sizes || []);
      setRevenueRanges(res.data.revenue_ranges || []);
    } catch (err) {
      console.error('Error fetching questions:', err);
      setError('Failed to load assessment questions');
    }
  };

  const totalSteps = dimensions.length + 2; // intro + profile + dimensions
  const currentDimension = step > 1 ? dimensions[step - 2] : null;
  const isLastDimension = step === dimensions.length + 1;

  // Scrape company URL
  const scrapeCompanyUrl = async () => {
    if (!profile.company_url) return;
    
    setScrapingUrl(true);
    try {
      const formData = new FormData();
      formData.append('url', profile.company_url);
      
      const res = await axios.post(`${API}/api/sustainability/scrape-report`, formData);
      setScrapedData(res.data);
    } catch (err) {
      console.error('Scraping error:', err);
    } finally {
      setScrapingUrl(false);
    }
  };

  // Send chat message
  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('message', userMessage);
      if (chatSessionId) formData.append('session_id', chatSessionId);
      if (profile.company_name) formData.append('company_name', profile.company_name);
      if (profile.industry) formData.append('industry', profile.industry);
      if (profile.region) formData.append('region', profile.region);
      
      const res = await axios.post(`${API}/api/sustainability/chat`, formData);
      
      if (!chatSessionId) setChatSessionId(res.data.session_id);
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      console.error('Chat error:', err);
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  // Submit assessment
  const submitAssessment = async () => {
    setLoading(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('company_name', profile.company_name);
      formData.append('company_url', profile.company_url);
      formData.append('industry', profile.industry);
      formData.append('region', profile.region);
      formData.append('company_size', profile.company_size);
      formData.append('annual_revenue', profile.annual_revenue);
      formData.append('respondent_name', profile.respondent_name);
      formData.append('respondent_role', profile.respondent_role);
      formData.append('respondent_email', profile.respondent_email);
      formData.append('responses', JSON.stringify(responses));
      
      if (additionalContext.trim()) {
        formData.append('additional_context', additionalContext.trim());
      }
      
      if (chatMessages.length > 0) {
        const chatContext = chatMessages.map(m => `${m.role === 'user' ? 'User' : 'Consultant'}: ${m.content}`).join('\n\n');
        formData.append('chatbot_context', chatContext);
      }
      
      if (scrapedData?.success) {
        formData.append('scraped_report_data', JSON.stringify(scrapedData));
      }

      const res = await submitMaturity(`${API}/api/sustainability/assess`, formData, {
        timeout: 180000
      });

      setResults(res.data);
      setStep(totalSteps);
    } catch (err) {
      console.error('Assessment error:', err);
      setError(err.response?.data?.detail || 'Failed to submit assessment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Verify secret code
  const verifyCode = async () => {
    if (secretCode.length !== 6) {
      setCodeError('Please enter a valid 6-digit code');
      return;
    }
    
    setLoading(true);
    setCodeError('');
    
    try {
      const formData = new FormData();
      formData.append('assessment_id', results.assessment_id || results.id);
      formData.append('secret_code', secretCode);
      // Use profile email, fall back to results email
      const email = profile.respondent_email || results.respondent_email;
      formData.append('email', email);
      
      const res = await axios.post(`${API}/api/sustainability/verify-code`, formData);
      
      if (res.data.verified) {
        setFullResults(res.data.full_report);
      }
    } catch (err) {
      // Handle different error response formats
      const detail = err.response?.data?.detail;
      let errorMessage = 'Invalid code. Please try again.';
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || e.message).join(', ') || errorMessage;
      } else if (detail && typeof detail === 'object') {
        errorMessage = detail.msg || detail.message || errorMessage;
      }
      
      setCodeError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Calculate progress
  const progress = ((step) / totalSteps) * 100;

  // Check if current step is complete
  const isStepComplete = () => {
    if (step === 0) return true;
    if (step === 1) {
      return profile.company_name && profile.industry && profile.region && 
             profile.company_size && profile.annual_revenue && 
             profile.respondent_name && profile.respondent_role && profile.respondent_email;
    }
    if (currentDimension) {
      return currentDimension.questions.every(q => responses[q.id] !== undefined);
    }
    return false;
  };

  // Score status helper
  const getScoreStatus = (score, benchmarkAvg) => {
    if (!benchmarkAvg) benchmarkAvg = 2.4;
    const gap = score - benchmarkAvg;
    
    if (gap < -0.8) return { status: 'Critical Gap', color: 'text-red-600', bg: 'bg-red-100' };
    if (gap < -0.3) return { status: 'Below Average', color: 'text-amber-600', bg: 'bg-amber-100' };
    if (gap < 0.3) return { status: 'At Par', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    return { status: 'Above Average', color: 'text-emerald-600', bg: 'bg-emerald-100' };
  };

  // Render intro
  const renderIntro = () => (
    <div className="max-w-3xl mx-auto text-center">
      <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mx-auto mb-6 shadow-lg">
        <Leaf className="w-10 h-10 text-white" />
      </div>
      <h1 className="text-4xl font-bold text-gray-900 mb-4">
        Sustainability / ESG Maturity Assessment
      </h1>
      <p className="text-lg text-gray-600 mb-8">
        Comprehensive assessment aligned with GRI, ISSB, BRSR, CSRD/ESRS, and regional frameworks.
        Evaluate your organization's ESG maturity across 10 key dimensions.
      </p>
      
      <div className="grid md:grid-cols-2 gap-6 mb-8 text-left">
        <div className="bg-emerald-50 rounded-xl p-6">
          <h3 className="font-semibold text-emerald-800 mb-3">What You'll Get</h3>
          <ul className="space-y-2 text-sm text-emerald-700">
            <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4" /> ESG Maturity Score (1-5 scale)</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Industry Benchmarking</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4" /> 12-Month Strategic Roadmap</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4" /> ROI Analysis & Business Case</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Region-Specific Compliance Guidance</li>
          </ul>
        </div>
        <div className="bg-teal-50 rounded-xl p-6">
          <h3 className="font-semibold text-teal-800 mb-3">Assessment Dimensions</h3>
          <ul className="space-y-2 text-sm text-teal-700">
            <li className="flex items-center gap-2"><FileText className="w-4 h-4" /> Reporting & Governance</li>
            <li className="flex items-center gap-2"><Cloud className="w-4 h-4" /> Climate & Emissions</li>
            <li className="flex items-center gap-2"><Recycle className="w-4 h-4" /> Resource Efficiency</li>
            <li className="flex items-center gap-2"><Users className="w-4 h-4" /> Workforce & Community</li>
            <li className="flex items-center gap-2"><Truck className="w-4 h-4" /> Supply Chain Sustainability</li>
          </ul>
        </div>
      </div>
      
      <Button
        onClick={() => setStep(1)}
        className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 px-8 py-6 text-lg"
        data-testid="btn-start"
      >
        Start Assessment
        <ChevronRight className="w-5 h-5 ml-2" />
      </Button>
      
      <p className="text-sm text-gray-500 mt-4">
        Takes approximately 10-15 minutes • 15 questions across 10 dimensions
      </p>
    </div>
  );

  // Render profile form
  const renderProfile = () => (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-lg">
        <div className="flex items-center gap-4 mb-6 pb-6 border-b">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
            <Building2 className="w-7 h-7 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Company Profile</h2>
            <p className="text-sm text-gray-600">Tell us about your organization</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Company Info */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
              <Input
                value={profile.company_name}
                onChange={(e) => setProfile({...profile, company_name: e.target.value})}
                placeholder="Enter company name"
                data-testid="input-company"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sustainability Report URL
                <span className="text-xs text-gray-500 ml-1">(Optional - we'll analyze it)</span>
              </label>
              <div className="flex gap-2">
                <Input
                  value={profile.company_url}
                  onChange={(e) => setProfile({...profile, company_url: e.target.value})}
                  placeholder="https://company.com/sustainability"
                  data-testid="input-url"
                />
                {profile.company_url && (
                  <Button
                    variant="outline"
                    onClick={scrapeCompanyUrl}
                    disabled={scrapingUrl}
                    className="shrink-0"
                  >
                    {scrapingUrl ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                  </Button>
                )}
              </div>
              {scrapedData?.success && (
                <p className="text-xs text-emerald-600 mt-1">✓ Report analyzed successfully</p>
              )}
            </div>
          </div>

          {/* Industry & Region */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Industry *</label>
              <Select value={profile.industry} onValueChange={(v) => setProfile({...profile, industry: v})}>
                <SelectTrigger data-testid="select-industry">
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent>
                  {industries.map((ind) => (
                    <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Region *</label>
              <Select value={profile.region} onValueChange={(v) => setProfile({...profile, region: v})}>
                <SelectTrigger data-testid="select-region">
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent>
                  {regions.map((reg) => (
                    <SelectItem key={reg.code} value={reg.code}>{reg.name} ({reg.framework})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Size & Revenue */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company Size *</label>
              <Select value={profile.company_size} onValueChange={(v) => setProfile({...profile, company_size: v})}>
                <SelectTrigger data-testid="select-size">
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent>
                  {companySizes.map((size) => (
                    <SelectItem key={size} value={size}>{size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Annual Revenue *</label>
              <Select value={profile.annual_revenue} onValueChange={(v) => setProfile({...profile, annual_revenue: v})}>
                <SelectTrigger data-testid="select-revenue">
                  <SelectValue placeholder="Select revenue" />
                </SelectTrigger>
                <SelectContent>
                  {revenueRanges.map((rev) => (
                    <SelectItem key={rev} value={rev}>{rev}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Respondent Info */}
          <div className="border-t pt-6">
            <h3 className="font-medium text-gray-900 mb-4">Respondent Information</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                <Input
                  value={profile.respondent_name}
                  onChange={(e) => setProfile({...profile, respondent_name: e.target.value})}
                  placeholder="Your name"
                  data-testid="input-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role/Title *</label>
                <Input
                  value={profile.respondent_role}
                  onChange={(e) => setProfile({...profile, respondent_role: e.target.value})}
                  placeholder="e.g., Chief Sustainability Officer"
                  data-testid="input-role"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <Input
                  type="email"
                  value={profile.respondent_email}
                  onChange={(e) => setProfile({...profile, respondent_email: e.target.value})}
                  placeholder="you@company.com"
                  data-testid="input-email"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render dimension questions
  const renderDimension = (dimension) => {
    const IconComponent = dimensionIcons[dimension.icon] || Target;
    
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-lg">
          {/* Dimension Header */}
          <div className="flex items-center gap-4 mb-6 pb-6 border-b">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center" style={{background: `linear-gradient(135deg, ${dimension.color}, ${dimension.color}dd)`}}>
              <IconComponent className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{dimension.name}</h2>
              <p className="text-sm text-gray-600">{dimension.description}</p>
            </div>
          </div>

          {/* Questions */}
          <div className="space-y-8">
            {dimension.questions.map((question, qIdx) => (
              <div key={question.id} className="space-y-4">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-sm font-semibold">
                    {qIdx + 1}
                  </span>
                  <p className="text-gray-800 font-medium leading-relaxed">{question.question}</p>
                </div>
                <div className="grid gap-2 pl-10">
                  {question.options.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setResponses({...responses, [question.id]: option.value})}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        responses[question.id] === option.value
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-emerald-300 hover:bg-gray-50'
                      }`}
                      data-testid={`option-${question.id}-${option.value}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          responses[question.id] === option.value
                            ? 'border-emerald-500 bg-emerald-500'
                            : 'border-gray-300'
                        }`}>
                          {responses[question.id] === option.value && (
                            <CheckCircle className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              responses[question.id] === option.value
                                ? 'bg-emerald-200 text-emerald-700'
                                : 'bg-gray-200 text-gray-600'
                            }`}>
                              Level {option.value}
                            </span>
                            <span className="font-medium text-gray-900">{option.label}</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Context Section - Show only on last dimension */}
        {isLastDimension && (
          <div className="mt-8">
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl overflow-hidden">
              <button
                onClick={() => setShowContextInput(!showContextInput)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-white/50 transition-colors"
                data-testid="toggle-context-input"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Additional Context (Optional)</h4>
                    <p className="text-sm text-gray-600">Share specific ESG challenges, initiatives, or context</p>
                  </div>
                </div>
                {showContextInput ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
              </button>
              {showContextInput && (
                <div className="px-6 pb-6">
                  <textarea
                    value={additionalContext}
                    onChange={(e) => setAdditionalContext(e.target.value)}
                    placeholder="Examples:&#10;• We have committed to net-zero by 2040&#10;• Currently using GRI for reporting&#10;• Key challenge is Scope 3 emissions measurement&#10;• Planning to implement TCFD recommendations&#10;• Supply chain due diligence is a priority"
                    className="w-full h-32 p-4 border border-gray-200 rounded-lg resize-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-gray-700 placeholder:text-gray-400"
                    maxLength={1500}
                    data-testid="additional-context-input"
                  />
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-xs text-gray-500">
                      This context helps our AI provide more relevant recommendations
                    </p>
                    <span className="text-xs text-gray-400">{additionalContext.length}/1500</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render results
  const renderResults = () => {
    if (!results) return null;
    
    const benchmark = results.industry_benchmark || { avg: 2.4, top: 4.2 };
    
    return (
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 mb-4 shadow-lg">
            <Leaf className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">ESG Maturity Assessment Results</h1>
          <p className="text-gray-600">{results.company_name} - {results.industry}</p>
          <p className="text-sm text-emerald-600 mt-1">Framework: {results.regional_framework?.name}</p>
          
          {fullResults && (
            <div className="mt-6">
              <Button
                onClick={() => generateSustainabilityPptx(results, fullResults)}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-8 py-3 text-lg shadow-lg"
                data-testid="btn-download-pptx"
              >
                <Download className="w-5 h-5 mr-2" />
                Download Executive Presentation
              </Button>
              <p className="text-sm text-gray-500 mt-2">PowerPoint presentation for board/CXO briefing</p>
            </div>
          )}
        </div>

        {/* Score Overview */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="col-span-2 bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
            <h3 className="text-sm font-medium text-gray-600 mb-4">Overall ESG Maturity Score</h3>
            <div className="flex items-end gap-6">
              <div className="text-5xl font-bold text-emerald-600">{(results.overall_score || 0).toFixed(1)}</div>
              <div className="text-gray-500 pb-2">/ 5.0</div>
              <div className="flex-1">
                <div className="text-lg font-semibold text-gray-900">{results.maturity_level?.name || results.overall_maturity_level}</div>
                <div className="text-sm text-gray-600">{results.maturity_level?.level}</div>
              </div>
            </div>
            <Progress value={((results.overall_score || 0) / 5) * 100} className="mt-4 h-3" />
          </div>
          
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-6">
            <h3 className="text-sm font-medium text-emerald-800 mb-4">Industry Comparison</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Your Score</span>
                <span className="font-bold text-emerald-600">{(results.overall_score || 0).toFixed(1)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Industry Avg</span>
                <span className="font-semibold text-gray-700">{benchmark.avg}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Top Performers</span>
                <span className="font-semibold text-emerald-600">{benchmark.top}</span>
              </div>
              <div className="pt-2 border-t">
                <span className={`text-sm font-medium ${(results.overall_score || 0) >= benchmark.avg ? 'text-emerald-600' : 'text-red-600'}`}>
                  {(results.overall_score || 0) >= benchmark.avg ? '↑' : '↓'} {Math.abs((results.overall_score || 0) - benchmark.avg).toFixed(2)} vs average
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Dimension Scores with How to Improve */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Dimension Analysis</h3>
          <div className="space-y-4">
            {results.dimension_scores?.map((dim) => {
              const status = getScoreStatus(dim.score, benchmark.avg);
              const IconComponent = dimensionIcons[dim.icon] || Target;
              const gapValue = ((dim.score || 0) - benchmark.avg).toFixed(2);
              
              // Get dimension-specific analysis from fullResults if available
              const dimAnalysis = fullResults?.ai_analysis?.dimension_analysis?.find(
                d => d.dimension === dim.dimension_name || d.dimension_id === dim.dimension_id
              ) || {};
              
              // Default improvement suggestions based on score
              const defaultImprovements = dim.score < 2 
                ? ['Establish baseline policies and governance', 'Begin data collection and measurement', 'Assign dedicated responsibility']
                : dim.score < 3 
                  ? ['Formalize processes and documentation', 'Set measurable targets', 'Implement regular monitoring']
                  : dim.score < 4
                    ? ['Align with industry best practices', 'Expand scope of initiatives', 'Pursue external verification']
                    : ['Lead industry initiatives', 'Share best practices', 'Drive innovation'];
              
              const improvements = dimAnalysis.recommendations || dimAnalysis.how_to_improve || defaultImprovements;
              
              return (
                <div key={dim.dimension_id} className={`p-4 rounded-xl border ${status.color === 'text-red-600' ? 'bg-red-50 border-red-200' : status.color === 'text-amber-600' ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
                  {/* Header Row */}
                  <div className="flex items-center gap-4 mb-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0`} style={{background: dim.color || '#10b981'}}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-gray-900">{dim.dimension_name}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${status.bg} ${status.color} font-medium`}>
                          {status.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Progress value={((dim.score || 0) / 5) * 100} className="flex-1 h-2" />
                        <span className="text-sm font-bold text-gray-700 w-12">{(dim.score || 0).toFixed(1)}/5</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Gap: <span className={parseFloat(gapValue) < 0 ? 'text-red-600 font-medium' : 'text-emerald-600 font-medium'}>{gapValue}</span> vs benchmark ({benchmark.avg})
                      </div>
                    </div>
                  </div>
                  
                  {/* How to Improve Section */}
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <h4 className="text-sm font-semibold text-emerald-700 mb-2 flex items-center gap-1">
                      <TrendingUp className="w-4 h-4" />
                      How to Improve
                    </h4>
                    <ul className="space-y-1">
                      {(Array.isArray(improvements) ? improvements : [improvements]).slice(0, 3).map((item, i) => (
                        <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                          <span>{typeof item === 'string' ? item : item.description || item.recommendation || 'Implement improvement measures'}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Code Verification */}
        {!fullResults ? (
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-8 mb-8">
            <div className="max-w-md mx-auto text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Unlock Full Report</h3>
              <p className="text-gray-600 mb-6">
                A secret code has been sent to <strong>{profile.respondent_email || results?.respondent_email || 'your email'}</strong>. 
                Enter it below to access your complete ESG report with roadmap, ROI analysis, and recommendations.
              </p>
              <div className="flex gap-3 justify-center">
                <Input
                  value={secretCode}
                  onChange={(e) => setSecretCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  className="w-40 text-center text-lg font-mono tracking-wider"
                  data-testid="input-secret-code"
                />
                <Button
                  onClick={verifyCode}
                  disabled={loading || secretCode.length !== 6}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="btn-verify-code"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
                </Button>
              </div>
              {codeError && <p className="text-red-500 text-sm mt-2">{codeError}</p>}
            </div>
          </div>
        ) : (
          /* Full Results */
          <div className="space-y-8">
            {/* Executive Summary */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-6 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Award className="w-6 h-6 text-emerald-600" />
                Executive Summary
              </h3>
              <div className="prose prose-emerald max-w-none">
                {typeof fullResults.ai_analysis?.executive_summary === 'string' 
                  ? fullResults.ai_analysis.executive_summary.split('\n').filter(p => p.trim()).map((para, i) => (
                      <p key={i} className="text-gray-700 mb-3 leading-relaxed">{para}</p>
                    ))
                  : <p className="text-gray-700">Assessment completed. Your ESG maturity score is {fullResults.overall_score?.toFixed(1)}/5.0.</p>
                }
              </div>
            </div>

            {/* Areas of Concern */}
            {fullResults.ai_analysis?.areas_of_concern && (
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Areas of Concern
                </h3>
                <div className="space-y-4">
                  {fullResults.ai_analysis.areas_of_concern.critical?.length > 0 && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                      <h4 className="font-semibold text-red-800 mb-2">🔴 Critical (Immediate Action Required)</h4>
                      <ul className="space-y-1">
                        {fullResults.ai_analysis.areas_of_concern.critical.map((item, i) => (
                          <li key={i} className="text-red-700 text-sm">• {item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {fullResults.ai_analysis.areas_of_concern.high_priority?.length > 0 && (
                    <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg">
                      <h4 className="font-semibold text-amber-800 mb-2">🟠 High Priority (Action within 3 months)</h4>
                      <ul className="space-y-1">
                        {fullResults.ai_analysis.areas_of_concern.high_priority.map((item, i) => (
                          <li key={i} className="text-amber-700 text-sm">• {item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {fullResults.ai_analysis.areas_of_concern.medium_priority?.length > 0 && (
                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
                      <h4 className="font-semibold text-yellow-800 mb-2">🟡 Medium Priority (Action within 6 months)</h4>
                      <ul className="space-y-1">
                        {fullResults.ai_analysis.areas_of_concern.medium_priority.map((item, i) => (
                          <li key={i} className="text-yellow-700 text-sm">• {item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 12-Month Strategic Roadmap */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Rocket className="w-5 h-5 text-emerald-600" />
                12-Month Strategic Roadmap
              </h3>
              <div className="grid md:grid-cols-4 gap-4">
                {[
                  { phase: 'Q1', name: 'Foundation', months: 'Months 1-3', focus: fullResults.ai_analysis?.strategic_roadmap?.phase_1?.focus_areas || ['ESG Governance Setup', 'Baseline Assessment', 'Policy Development'], color: 'bg-emerald-500' },
                  { phase: 'Q2', name: 'Build', months: 'Months 4-6', focus: fullResults.ai_analysis?.strategic_roadmap?.phase_2?.focus_areas || ['Data Collection Systems', 'Emissions Inventory', 'Training Programs'], color: 'bg-teal-500' },
                  { phase: 'Q3', name: 'Integrate', months: 'Months 7-9', focus: fullResults.ai_analysis?.strategic_roadmap?.phase_3?.focus_areas || ['Framework Alignment', 'Supplier Engagement', 'Risk Assessment'], color: 'bg-green-500' },
                  { phase: 'Q4', name: 'Optimize', months: 'Months 10-12', focus: fullResults.ai_analysis?.strategic_roadmap?.phase_4?.focus_areas || ['Performance Tracking', 'External Assurance', 'Continuous Improvement'], color: 'bg-lime-600' }
                ].map((q, i) => (
                  <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className={`${q.color} text-white p-3 text-center`}>
                      <div className="text-xs opacity-80">{q.phase}</div>
                      <div className="font-bold">{q.name}</div>
                      <div className="text-xs opacity-80">{q.months}</div>
                    </div>
                    <div className="p-3 bg-gray-50">
                      <ul className="space-y-1">
                        {(Array.isArray(q.focus) ? q.focus : [q.focus]).slice(0, 3).map((item, j) => (
                          <li key={j} className="text-xs text-gray-600 flex items-start gap-1">
                            <CheckCircle className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ROI Analysis */}
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-200 rounded-2xl p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-teal-600" />
                ROI Analysis & Business Value
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-xl p-4 border border-teal-200">
                  <div className="text-xs text-gray-500 mb-1">Estimated Investment</div>
                  <div data-testid="sustainability-investment" className="text-lg font-bold text-teal-700">{fullResults.ai_analysis?.roi_analysis?.total_investment || 'Not available'}</div>
                  <div className="text-xs text-gray-500 mt-1">12-month program</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-emerald-200">
                  <div className="text-xs text-gray-500 mb-1">Cost Savings</div>
                  <div data-testid="sustainability-savings" className="text-lg font-bold text-emerald-700">{fullResults.ai_analysis?.roi_analysis?.cost_savings || 'Not available'}</div>
                  <div className="text-xs text-gray-500 mt-1">Operational efficiency</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-green-200">
                  <div className="text-xs text-gray-500 mb-1">Risk Mitigation</div>
                  <div data-testid="sustainability-risk-value" className="text-lg font-bold text-green-700">{fullResults.ai_analysis?.roi_analysis?.risk_mitigation_value || 'Not available'}</div>
                  <div className="text-xs text-gray-500 mt-1">Avoided penalties</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-lime-200">
                  <div className="text-xs text-gray-500 mb-1">Revenue Impact</div>
                  <div data-testid="sustainability-revenue-impact" className="text-lg font-bold text-lime-700">{fullResults.ai_analysis?.roi_analysis?.revenue_impact || 'Not available'}</div>
                  <div className="text-xs text-gray-500 mt-1">Green premium & access</div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-teal-200">
                <h4 className="font-semibold text-gray-800 mb-3">Business Value Categories</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="text-sm font-medium text-emerald-700 mb-2">Operational Benefits</h5>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li>• Reduced energy and resource costs</li>
                      <li>• Streamlined compliance processes</li>
                      <li>• Lower waste disposal expenses</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium text-teal-700 mb-2">Strategic Benefits</h5>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li>• Improved ESG ratings & investor access</li>
                      <li>• Enhanced brand reputation</li>
                      <li>• Talent attraction & retention</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Risk Mitigation Strategies */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                Risk Mitigation Strategies
              </h3>
              <div className="space-y-3">
                {(fullResults.ai_analysis?.risk_mitigation || [
                  { risk: 'Regulatory Compliance Risk', strategy: 'Implement comprehensive ESG management system aligned with regional frameworks', priority: 'High' },
                  { risk: 'Climate Physical Risk', strategy: 'Conduct site-level vulnerability assessments and develop adaptation plans', priority: 'High' },
                  { risk: 'Supply Chain Risk', strategy: 'Implement tiered supplier ESG assessment program with capacity building', priority: 'Medium' },
                  { risk: 'Reputational Risk', strategy: 'Establish transparent ESG communication and stakeholder engagement', priority: 'Medium' }
                ]).slice(0, 4).map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      item.priority === 'High' ? 'bg-red-100 text-red-700' : 
                      item.priority === 'Medium' ? 'bg-amber-100 text-amber-700' : 
                      'bg-green-100 text-green-700'
                    }`}>
                      {item.priority}
                    </span>
                    <div>
                      <h4 className="font-medium text-gray-900">{item.risk}</h4>
                      <p className="text-sm text-gray-600 mt-1">{item.strategy}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Use Cases */}
            {fullResults.ai_analysis?.use_cases?.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-emerald-600" />
                  Recommended Use Cases
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {fullResults.ai_analysis.use_cases.slice(0, 4).map((uc, i) => (
                    <div key={i} className="bg-emerald-50 rounded-xl p-4">
                      <h4 className="font-semibold text-emerald-900">{uc.title || `Use Case ${i + 1}`}</h4>
                      <p className="text-sm text-emerald-700 mt-1">{uc.description}</p>
                      {uc.impact && <p className="text-xs text-emerald-600 mt-2">Impact: {uc.impact}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Regional Framework Compliance */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-600" />
                Regional Framework Compliance: {results.regional_framework?.name}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">Key Requirements</h4>
                  <ul className="space-y-2">
                    {(results.regional_framework?.key_requirements || results.regional_framework?.key_principles || [
                      'Comprehensive ESG disclosure',
                      'Climate-related financial disclosures',
                      'Supply chain due diligence',
                      'Stakeholder engagement reporting'
                    ]).slice(0, 4).map((req, i) => (
                      <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">Compliance Status</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                      <span className="text-sm text-gray-600">Overall Readiness</span>
                      <span className={`text-sm font-medium ${(results.overall_score || 0) >= 3 ? 'text-green-600' : (results.overall_score || 0) >= 2 ? 'text-amber-600' : 'text-red-600'}`}>
                        {(results.overall_score || 0) >= 3 ? 'On Track' : (results.overall_score || 0) >= 2 ? 'Needs Work' : 'At Risk'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 p-2">
                      Based on your assessment score of {(results.overall_score || 0).toFixed(1)}/5.0 against {results.regional_framework?.name || 'regional'} requirements.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Level Up Section - Journey to Next ESG Maturity Level */}
        {fullResults?.ai_analysis?.level_progression && (
          <div className="bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 rounded-2xl shadow-lg p-8 mt-8 border-2 border-emerald-200" data-testid="level-up-section">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                <ArrowUpRight className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Level Up: Journey to Next ESG Maturity Level</h2>
                <p className="text-gray-600">What it takes to advance your sustainability performance</p>
              </div>
            </div>

            {/* Current to Next Level Visualization */}
            <div className="bg-white rounded-xl p-6 mb-8 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <div className="text-center flex-1">
                  <div className="w-20 h-20 mx-auto bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mb-3 shadow-lg">
                    <span className="text-3xl font-bold text-white">
                      {fullResults.ai_analysis.level_progression.current_level?.level_number || Math.floor(fullResults.overall_score)}
                    </span>
                  </div>
                  <div className="font-bold text-gray-900">Current Level</div>
                  <div className="text-sm text-gray-500">
                    {fullResults.ai_analysis.level_progression.current_level?.level_name || fullResults.maturity_level?.name}
                  </div>
                </div>
                <div className="flex-1 px-4">
                  <div className="relative">
                    <div className="h-2 bg-gradient-to-r from-amber-400 via-emerald-400 to-emerald-500 rounded-full" />
                    <div className="absolute top-4 left-1/2 transform -translate-x-1/2 text-center">
                      <div className="flex items-center gap-1 bg-emerald-100 px-3 py-1 rounded-full">
                        <Clock className="w-3 h-3 text-emerald-600" />
                        <span className="text-xs font-medium text-emerald-700">
                          {fullResults.ai_analysis.level_progression.progression_requirements?.time_estimate || '9-12 months'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-center flex-1">
                  <div className="w-20 h-20 mx-auto bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mb-3 shadow-lg">
                    <span className="text-3xl font-bold text-white">
                      {fullResults.ai_analysis.level_progression.next_level?.level_number || Math.min(Math.floor(fullResults.overall_score) + 1, 5)}
                    </span>
                  </div>
                  <div className="font-bold text-gray-900">Target Level</div>
                  <div className="text-sm text-emerald-600 font-medium">
                    {fullResults.ai_analysis.level_progression.next_level?.level_name || 'Next Level'}
                  </div>
                </div>
              </div>
            </div>

            {/* Resource Requirements Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-xl p-5 shadow-sm border border-emerald-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="font-bold text-gray-900">Time</div>
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  {fullResults.ai_analysis.level_progression.progression_requirements?.time_estimate || '9-12 months'}
                </div>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border border-emerald-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="font-bold text-gray-900">Manpower</div>
                </div>
                <div className="text-lg font-bold text-purple-600">
                  {fullResults.ai_analysis.level_progression.progression_requirements?.manpower_resources?.internal_fte || 'Not available'}
                </div>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border border-emerald-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="font-bold text-gray-900">Investment</div>
                </div>
                <div className="text-lg font-bold text-emerald-600">
                  {fullResults.ai_analysis.level_progression.progression_requirements?.investment_required?.total_range || 'Not available'}
                </div>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border border-emerald-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="font-bold text-gray-900">Expected Value</div>
                </div>
                <div className="text-lg font-bold text-amber-600">
                  {fullResults.ai_analysis.level_progression.progression_requirements?.business_benefits?.quantified_value || 'Not available'}
                </div>
              </div>
            </div>

            {/* Milestones */}
            {fullResults.ai_analysis.level_progression.milestones?.length > 0 && (
              <div className="bg-white rounded-xl p-6 mb-8 shadow-sm">
                <h3 className="font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Flag className="w-5 h-5 text-emerald-600" />
                  Key Milestones to Next Level
                </h3>
                <div className="relative">
                  <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-gradient-to-b from-emerald-400 to-teal-500" />
                  <div className="space-y-6">
                    {fullResults.ai_analysis.level_progression.milestones.map((milestone, idx) => (
                      <div key={idx} className="relative flex gap-4">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 z-10 bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg">
                          <span className="text-white font-bold">{milestone.milestone_number || idx + 1}</span>
                        </div>
                        <div className="flex-1 bg-gradient-to-r from-emerald-50 to-transparent rounded-lg p-4 border border-emerald-100">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-bold text-gray-900">{milestone.name}</h4>
                            <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {milestone.target_month}
                            </span>
                          </div>
                          {milestone.key_deliverables?.length > 0 && (
                            <div className="text-sm text-gray-600 mb-2">
                              <strong>Deliverables:</strong>
                              <ul className="list-disc list-inside ml-2 mt-1">
                                {milestone.key_deliverables.slice(0, 3).map((d, i) => (
                                  <li key={i} className="text-gray-600">{d}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {milestone.success_criteria && (
                            <div className="text-xs text-emerald-600 font-medium">
                              ✓ Success: {milestone.success_criteria}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Business Benefits at Next Level */}
            <div className="bg-white rounded-xl p-6 mb-8 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                Business Benefits at Next ESG Level
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-emerald-50 to-white rounded-lg p-4 border border-emerald-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Leaf className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm font-medium text-gray-600">Carbon Reduction</span>
                  </div>
                  <div className="text-lg font-bold text-emerald-700">
                    {fullResults.ai_analysis.level_progression.progression_requirements?.business_benefits?.efficiency_gains || 'Not available'}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-white rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-gray-600">ESG Rating Impact</span>
                  </div>
                  <div className="text-lg font-bold text-blue-700">
                    {fullResults.ai_analysis.level_progression.progression_requirements?.business_benefits?.revenue_impact || 'Not available'}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-white rounded-lg p-4 border border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-gray-600">Risk Mitigation</span>
                  </div>
                  <div className="text-lg font-bold text-amber-700">
                    {fullResults.ai_analysis.level_progression.progression_requirements?.business_benefits?.risk_reduction || 'Not available'}
                  </div>
                </div>
              </div>
            </div>

            {/* Key Challenges & Quick Wins */}
            <div className="grid md:grid-cols-2 gap-6">
              {fullResults.ai_analysis.level_progression.key_challenges?.length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-amber-100">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    Key Challenges
                  </h3>
                  <ul className="space-y-2">
                    {fullResults.ai_analysis.level_progression.key_challenges.map((challenge, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                        <div className="w-5 h-5 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-amber-700 text-xs font-bold">{idx + 1}</span>
                        </div>
                        {challenge}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {fullResults.ai_analysis.level_progression.quick_wins_to_accelerate?.length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-emerald-100">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Rocket className="w-5 h-5 text-emerald-600" />
                    Quick Wins to Accelerate
                  </h3>
                  <ul className="space-y-2">
                    {fullResults.ai_analysis.level_progression.quick_wins_to_accelerate.map((win, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        {win}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* AGI Insights */}
        <div className="mt-8" data-testid="agi-insights-section">
          <AGIInsightsTab
            assessmentType="Sustainability"
            assessmentId={results?.assessment_id || results?.id}
          />
        </div>

        <ReportSecurityNote />

        {/* Actions */}
        <div className="flex flex-wrap justify-center gap-4 mt-8">
          <Button variant="outline" onClick={() => navigate('/')} className="gap-2">
            <Home className="w-4 h-4" />
            Back to Home
          </Button>
          {fullResults && (
            <Button
              onClick={() => generateSustainabilityPptx(results, fullResults)}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 gap-2"
              data-testid="btn-download-pptx-bottom"
            >
              <Download className="w-4 h-4" />
              Download Presentation
            </Button>
          )}
          <Button
            onClick={() => {
              setStep(0);
              setResults(null);
              setFullResults(null);
              setResponses({});
              setProfile({
                company_name: '',
                company_url: '',
                industry: '',
                region: '',
                company_size: '',
                annual_revenue: '',
                respondent_name: '',
                respondent_role: '',
                respondent_email: ''
              });
            }}
            className="bg-gray-800 hover:bg-gray-900 gap-2"
          >
            <ArrowRight className="w-4 h-4" />
            Start New Assessment
          </Button>
        </div>
      </div>
    );
  };

  // Loading state when loading assessment from URL
  if (loadingAssessment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-600 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Loading Your Assessment...</h2>
          <p className="text-gray-500 mt-2">Please wait while we retrieve your results</p>
        </div>
      </div>
    );
  }

  // Main render
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center">
                <Leaf className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-gray-900">SAMANVAI</span>
            </Link>
            
            {step > 0 && step < totalSteps && (
              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-600">
                  Step {step} of {totalSteps - 1}
                </div>
                <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {error && (
          <div className="max-w-3xl mx-auto mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Render current step */}
        {step === 0 && renderIntro()}
        {step === 1 && renderProfile()}
        {step > 1 && step < totalSteps && currentDimension && renderDimension(currentDimension)}
        {(step === totalSteps || step >= 999) && renderResults()}

        {/* Navigation Buttons */}
        {step > 0 && step < totalSteps && (
          <div className="max-w-3xl mx-auto mt-8 flex justify-between">
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="gap-2"
              data-testid="btn-back"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </Button>
            
            {isLastDimension ? (
              <Button
                onClick={submitAssessment}
                disabled={loading || !isStepComplete()}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 gap-2"
                data-testid="btn-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating Report...
                  </>
                ) : (
                  <>
                    Generate ESG Report
                    <Rocket className="w-4 h-4" />
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!isStepComplete()}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 gap-2"
                data-testid="btn-next"
              >
                {step === 1 ? 'Continue' : 'Next'}
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default SustainabilityPage;
